import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTeam } from '@/hooks/useTeam'
import { useMyTeam } from '@/hooks/useMyTeam'
import Logo from '@/components/common/Logo'

function AppNav() {
  const { signOut } = useAuth()
  const { team } = useTeam()
  const { usesTeamEmail } = useMyTeam()
  const location = useLocation()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth?tab=signin')
  }

  // Scrimmage items first. The parent-email tools (team email, parents, team info) show here only for
  // accounts that already use them; everyone else reaches them from Settings > Team email tools.
  const scrimmageLinks = [
    { to: '/scrimmages', label: 'Find scrimmages', short: 'Find', icon: '🔍' },
    { to: '/scrimmages?view=ice', label: 'Open ice', short: 'Ice', icon: '🧊' },
    { to: '/my-team', label: 'My team', icon: '🏒' },
    { to: '/alerts', label: 'Alerts', icon: '🔔' },
  ]
  const emailLinks = usesTeamEmail
    ? [
        { to: '/dashboard', label: 'Team email', icon: '✉' },
        { to: '/parents', label: 'Parents', icon: '👪' },
        { to: '/wiki', label: 'Team info', icon: '📋' },
      ]
    : []
  const links = [...scrimmageLinks, ...emailLinks]
  // Mobile tabs: one row, so the long-tail items (Parents, Team info) live under Settings > Team email tools.
  const mobileTabs = [
    ...scrimmageLinks,
    ...(usesTeamEmail ? [{ to: '/dashboard', label: 'Email', icon: '✉' }] : []),
    { to: '/settings', label: 'Settings', icon: '⚙' },
  ]

  const isActive = (to) => {
    const [path, query] = to.split('?')
    if (path === '/scrimmages') {
      // "Find scrimmages" and "Open ice" share a path; tell them apart by ?view=ice.
      const ice = new URLSearchParams(location.search).get('view') === 'ice'
      return location.pathname === '/scrimmages' && (query ? ice : !ice)
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <nav className="bg-white border-b border-border sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <Link to="/my-team" className="flex items-center gap-2">
              <Logo size="sm" />
            </Link>
            <div className="hidden sm:flex items-center gap-1">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    isActive(link.to)
                      ? 'bg-brand-green/10 text-brand-green'
                      : 'text-brand-slate hover:text-brand-charcoal hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {team?.url_slug && (
                <a
                  href={`/p/${team.url_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-md text-sm font-medium text-brand-slate hover:text-brand-charcoal hover:bg-gray-50 transition"
                >
                  Portal ↗
                </a>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/settings"
              className={`p-2 rounded-md text-sm transition ${
                isActive('/settings')
                  ? 'text-brand-green bg-brand-green/10'
                  : 'text-brand-slate hover:text-brand-charcoal hover:bg-gray-50'
              }`}
              title="Settings"
            >
              ⚙
            </Link>
            <button
              onClick={handleSignOut}
              className="p-2 rounded-md text-sm text-brand-slate hover:text-brand-charcoal hover:bg-gray-50 transition"
              title="Sign out"
            >
              ↪
            </button>
          </div>
        </div>
      </div>

      {/* Mobile bottom tabs */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-border z-50">
        <div className="flex">
          {mobileTabs.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex-1 min-w-0 flex flex-col items-center py-2 text-[11px] ${
                isActive(item.to) ? 'text-brand-green' : 'text-brand-slate'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{item.short || item.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </nav>
  )
}

export default AppNav
